import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Select,
  MenuItem,
  TextField,
  Tooltip,
  InputAdornment,
  Autocomplete,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { NumericFormat } from "react-number-format";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { stateList } from "./optionsHelper";
import { useBondCharacteristicStore } from "../../Store/bondCharacteristicStore";
import { useBondLadderStore } from "../../Store/bondLadderStore";
import { tokens } from "../../Common/designTokens"; // added
import {
  maturityDateToTenorYears,
  MATURITY_TENOR_PADDING_DAYS,
} from "../../Utils/filterUtils";
dayjs.extend(utc);

const LabeledField = ({
  readOnly,
  checked,
  label,
  onCheckboxChange,
  children,
  datePreview, // Add new prop for date preview
}) => (
  <Box
    sx={{
      display: "grid",
      // Reduce label width to decrease space between label and input
      gridTemplateColumns: checked ? "180px 1fr" : "180px",
      // keep a small gap between columns
      columnGap: "0px",
      alignItems: "center",
      height: datePreview ? "75px" : "60px", // Increase height when preview exists
      // borderBottom: "1px solid rgba(0, 0, 0, 0.2)",
      borderBottom: `1px solid ${tokens.colors.bodyBorderColor50}`,
    }}
  >
    <Box>
      <FormControlLabel
        control={
          <Checkbox
            checked={checked}
            onChange={readOnly ? undefined : onCheckboxChange}
            disabled={readOnly}
            sx={{
              "&.Mui-disabled": {
                // color: "primary.main",
                color: tokens.palette.primary,
                opacity: 1,
              },
            }}
          />
        }
        label={
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: checked ? "bold" : "normal", color: tokens.colors.fontColour }}
          >
            {label}
          </Typography>
        }
        sx={{ m: 0 }}
      />
      {datePreview && checked && (
        <Typography
          variant="body2"
          sx={{
            fontSize: "14px",
            fontWeight: 500,
            ml: 4,
            mt: -0.5,
            pl: 1, // <-- Add left padding for the preview
            color: tokens.colors.fontColour85,
          }}
        >
          {datePreview}
        </Typography>
      )}
    </Box>
    {checked && (
      <Box sx={{ display: "flex", alignItems: "center" }}>{children}</Box>
    )}
  </Box>
);

const BondCharacteristicField = ({
  readOnly = false,
  panelName = "bondCharacteristic",
  field,
  checked,
  subtab,
  onCheckboxChange,
  bondCharacteristicData,
  setBondCharacteristicData,
  handleBondCharacteristicChange,
  fieldOptions,
  inputStyle,
  onValidationChange,
  issuerOptions = [],
  moodyRatingOptions = [],
  spRatingOptions = [],
  securityChargeOptions = [],
}) => {
  const [activeField, setActiveField] = useState(null);
  const [isSecurityChargeOpen, setIsSecurityChargeOpen] = useState(false);
  const [dateErrors, setDateErrors] = useState({});
  const [rangeErrors, setRangeErrors] = useState({}); // New state for numeric range errors
  const [maturityDatePreview, setMaturityDatePreview] = useState(""); // New state for maturity date preview
  const [issuerValidationError, setIssuerValidationError] = useState(false);
  const [inputValue, setInputValue] = useState(bondCharacteristicData.issuer || "");

  // Get store setters based on panel name - memoize to prevent re-creation
  const bondCharacteristicStore = useBondCharacteristicStore();
  const bondLadderStore = useBondLadderStore();
  
  const setValidationError = useMemo(() => 
    panelName === "bondLadder" 
      ? bondLadderStore.setValidationError 
      : bondCharacteristicStore.setValidationError,
    [panelName, bondLadderStore.setValidationError, bondCharacteristicStore.setValidationError]
  );

  // Helper to check if a value exists.
  const hasValue = (value) =>
    value !== undefined && value !== null && value !== "";
      // Strip trailing Y (e.g. "1Y" -> 1) for maturity range
  // paddingDays inverts this component's own -90/+90 preview padding so an absolute date
  // round-trips back to itself: +90 for the lower bound, -90 for the upper. Only applies
  // to dates — a stored "5Y" is a plain tenor and the padding is intended there.
  const stripYear = (v, paddingDays = 0) => {
    if (v === null || v === undefined || v === '') return '';
    if (typeof v === 'number') return v;
    const raw = String(v).trim();
    const s = raw.toUpperCase();
    if (/^\d+(\.\d+)?Y$/.test(s)) return Number(s.replace('Y', ''));
    if (!isNaN(Number(s))) return Number(s);
    const years = maturityDateToTenorYears(raw, paddingDays);
    return years === null ? '' : years;
  };
  // Calculate maturity date preview - optimize dependencies
  useEffect(() => {
    if (field !== "maturityRanges") return;
    
    const calculateDatePreview = () => {
      // Normalize maturity values (strip trailing 'Y' etc.) before using
      const rawMin = bondCharacteristicData.maturityMin;
      const rawMax = bondCharacteristicData.maturityMax;
      const maturityMinNormalized = stripYear(rawMin, MATURITY_TENOR_PADDING_DAYS);
      const maturityMaxNormalized = stripYear(rawMax, -MATURITY_TENOR_PADDING_DAYS);

      if (!hasValue(maturityMinNormalized) && !hasValue(maturityMaxNormalized)) {
        return "";
      }

      // The following uses UTC for all calculations
      const today = dayjs().utc();
      let fromDate = null;
      let toDate = null;

      if (hasValue(maturityMinNormalized)) {
        const calculatedMin = today.add(Number(maturityMinNormalized) * 365, "day").subtract(90, "day");
        fromDate = calculatedMin.isBefore(today) ? today : calculatedMin;
      }

      if (hasValue(maturityMaxNormalized)) {
        toDate = today.add(Number(maturityMaxNormalized) * 365, "day").add(90, "day");
      }

      const fromDateStr = fromDate ? fromDate.format("D MMM YYYY") : "";
      const toDateStr = toDate ? toDate.format("D MMM YYYY") : "";

      if (fromDateStr && toDateStr) {
        return `(${fromDateStr} to ${toDateStr})`;
      } else if (fromDateStr) {
        return `(${fromDateStr} to ...)`;
      } else if (toDateStr) {
        return `(... to ${toDateStr})`;
      }

      return "";
    };

    setMaturityDatePreview(calculateDatePreview());
  }, [bondCharacteristicData.maturityMin, bondCharacteristicData.maturityMax, field]);

  // Validate issuer input - optimize dependencies
  useEffect(() => {
    if (field !== "issuer") return;
    
    const issuerValue = bondCharacteristicData.issuer;
    if (issuerValue && issuerValue.trim() !== "") {
      const hasMatch = issuerOptions.some(option => 
        option.toLowerCase() === issuerValue.toLowerCase()
      );
      setIssuerValidationError(!hasMatch);
    } else {
      setIssuerValidationError(false);
    }
  }, [bondCharacteristicData.issuer, issuerOptions, field]);

  // Memoize validation logic to prevent unnecessary recalculations
  const validationResults = useMemo(() => {
    // Don't perform validation if we're not going to render this field
    if (field === "minimumRating" && subtab === "Treasury") {
      return { errors: {}, numericErrors: {}, hasFieldErrors: false };
    }

    const errors = {};
    const numericErrors = {};

    // Add issuer validation to the validation check
    if (field === "issuer" && issuerValidationError) {
      numericErrors.issuer = "No matching issuer found";
    }

    // Validate numeric ranges
    if (field === "couponRange") {
      const { couponMin, couponMax } = bondCharacteristicData;
      if (
        hasValue(couponMin) &&
        hasValue(couponMax) &&
        parseFloat(couponMin) > parseFloat(couponMax)
      ) {
        numericErrors.couponMin = "Minimum coupon cannot be greater than maximum";
        numericErrors.couponMax = "Maximum coupon cannot be less than minimum";
      }
    }

    if (field === "price") {
      const { priceMin, priceMax } = bondCharacteristicData;
      if (
        hasValue(priceMin) &&
        hasValue(priceMax) &&
        parseFloat(priceMin) > parseFloat(priceMax)
      ) {
        numericErrors.priceMin = "Minimum price cannot be greater than maximum";
        numericErrors.priceMax = "Maximum price cannot be less than minimum";
      }
    }

    if (field === "yield") {
      const { yieldMin, yieldMax } = bondCharacteristicData;
      if (
        hasValue(yieldMin) &&
        hasValue(yieldMax) &&
        parseFloat(yieldMin) > parseFloat(yieldMax)
      ) {
        numericErrors.yieldMin = "Minimum yield cannot be greater than maximum";
        numericErrors.yieldMax = "Maximum yield cannot be less than minimum";
      }
    }

    if (field === "maturityRanges") {
      const { maturityMin, maturityMax } = bondCharacteristicData;
      if (
        hasValue(maturityMin) &&
        hasValue(maturityMax) &&
        parseFloat(maturityMin) > parseFloat(maturityMax)
      ) {
        numericErrors.maturityMin = "Minimum maturity cannot be greater than maximum";
        numericErrors.maturityMax = "Maximum maturity cannot be less than minimum";
      }
    }

    const hasFieldErrors = Object.keys(errors).length > 0 || Object.keys(numericErrors).length > 0;

    return { errors, numericErrors, hasFieldErrors };
  }, [
    field, 
    subtab, 
    issuerValidationError,
    bondCharacteristicData.couponMin,
    bondCharacteristicData.couponMax,
    bondCharacteristicData.priceMin,
    bondCharacteristicData.priceMax,
    bondCharacteristicData.yieldMin,
    bondCharacteristicData.yieldMax,
    bondCharacteristicData.maturityMin,
    bondCharacteristicData.maturityMax
  ]);

  // Update local state when validation results change
  useEffect(() => {
    setDateErrors(validationResults.errors);
    setRangeErrors(validationResults.numericErrors);
  }, [validationResults]);

  // Separate effect for store updates to prevent circular dependencies
  useEffect(() => {
    // Update store validation errors
    if (field === "couponRange") {
      setValidationError('couponMin', !!validationResults.numericErrors.couponMin);
      setValidationError('couponMax', !!validationResults.numericErrors.couponMax);
    } else if (field === "price") {
      setValidationError('priceMin', !!validationResults.numericErrors.priceMin);
      setValidationError('priceMax', !!validationResults.numericErrors.priceMax);
    } else if (field === "yield") {
      setValidationError('yieldMin', !!validationResults.numericErrors.yieldMin);
      setValidationError('yieldMax', !!validationResults.numericErrors.yieldMax);
    } else if (field === "maturityRanges") {
      setValidationError('maturityMin', !!validationResults.numericErrors.maturityMin);
      setValidationError('maturityMax', !!validationResults.numericErrors.maturityMax);
    } else if (field === "issuer") {
      setValidationError('issuer', issuerValidationError);
    } else {
      setValidationError(field, validationResults.hasFieldErrors);
    }
  }, [
    field, 
    validationResults.numericErrors.couponMin,
    validationResults.numericErrors.couponMax,
    validationResults.numericErrors.priceMin,
    validationResults.numericErrors.priceMax,
    validationResults.numericErrors.yieldMin,
    validationResults.numericErrors.yieldMax,
    validationResults.numericErrors.maturityMin,
    validationResults.numericErrors.maturityMax,
    validationResults.hasFieldErrors,
    issuerValidationError,
    setValidationError
  ]);

  // Separate effect for parent validation callback
  useEffect(() => {
    if (!onValidationChange) return;

    // Report validation errors to parent component to prevent form submission
    const hasErrors = validationResults.hasFieldErrors;
    
    // Store the validation state in a global window variable for access in submit handler
    if (typeof window !== "undefined") {
      if (!window.bondValidationErrors) {
        window.bondValidationErrors = {};
      }
      window.bondValidationErrors[field] = hasErrors;

      // Calculate overall validation status
      const anyErrors = Object.values(window.bondValidationErrors).some(Boolean);
      onValidationChange(anyErrors);
    } else {
      onValidationChange(hasErrors);
    }
  }, [validationResults.hasFieldErrors, field, onValidationChange]);

  // Clear field-specific validation error when component unmounts
  useEffect(() => {
    return () => {
      if (typeof window !== "undefined" && window.bondValidationErrors) {
        delete window.bondValidationErrors[field];
      }
    };
  }, [field]);

  // Handle focus to track active field
  const handleFieldFocus = (name) => () => {
    setActiveField(name);
  };

  const handleFieldBlur = () => {
    setActiveField(null);
  };

  // Skip rendering Minimum Rating field when bond type is Treasury
  // This conditional return must come after all hook calls
  if ((field === "minimumRating" && subtab === "Treasury") || 
      (field === "issuer" && subtab !== "Corporates") ||
      (field === "minimumRatingMoody" && subtab === "Treasury")) {
    return null;
  }

  // Determine checked state.
  let isChecked;
  if (readOnly) {
    switch (field) {
      case "couponRange":
        isChecked =
          hasValue(bondCharacteristicData.couponMin) ||
          hasValue(bondCharacteristicData.couponMax);
        break;
      case "price":
      case "yield":
        isChecked =
          hasValue(bondCharacteristicData[`${field}Min`]) ||
          hasValue(bondCharacteristicData[`${field}Max`]);
        break;
      case "maturityRanges":
        isChecked =
          hasValue(bondCharacteristicData.maturityMin) ||
          hasValue(bondCharacteristicData.maturityMax);
        break;
      case "securityCharge":
        isChecked = Array.isArray(bondCharacteristicData.securityCharge)
          ? bondCharacteristicData.securityCharge.length > 0
          : hasValue(bondCharacteristicData.securityCharge);
        break;
      default:
        isChecked = hasValue(bondCharacteristicData[field]);
        break;
    }
  } else {
    isChecked = (checked[panelName] && checked[panelName][field]) || false;
  }

  // Only render the field if it's checked in readonly mode.
  if (readOnly && !isChecked) {
    return null;
  }

  // Field labels
  const getFieldLabel = () => {
    const labels = {
      minimumRating: "Min S&P Rating",
      couponRange: "Coupon Range",
      maturityRanges: "Maturity Range",
      duration: "Duration",
      price: "Price",
      yield: "YTM",
      zeroCoupon: "Zero Coupon",
      callable: "Callable",
      state: "State",
      underlyingRating: "Underlying Rating of the issuer",
      insured: "Insured",
      taxable: "Taxable",
      paymentFrequency: "Payment Frequency",
      incomeSource: "Income Source",
      altMinTax: "Alt Min Tax",
      securityCharge: "Factored Securities",
      sector: "Sector",
      nextCallDate: "Next call date",
      issuer: "Issuer", // Add label for issuer
      minimumRatingMoody: "Min Moody's Rating",
    };
    return labels[field] || field;
  };

  // Common styles.
  const commonTextFieldStyle = {
    backgroundColor: tokens.colors.white,
    border: `1px solid ${tokens.colors.bodyBorderColor50}`,
    borderRadius: "8px",
    height: "40px",
    // remove extra right padding on input roots (overrides generated MUI class padding-right:14px)
    paddingRight: "0px !important",
    // ensure inner input and input root also have zero right padding
    "& .MuiInputBase-root": {
      paddingRight: "0px !important",
    },
    "& input": {
      paddingRight: "0px !important",
      color: tokens.colors.fontColour,
    },
  };

  const enhancedDisabledStyle = {
    "&.Mui-disabled": {
      color: tokens.colors.disabledFontColour,
      WebkitTextFillColor: tokens.colors.disabledFontColour,
      opacity: 1,
      backgroundColor: tokens.colors.defaultGridRowColor,
    },
    "& .MuiFilledInput-root.Mui-disabled": {
      backgroundColor: tokens.colors.defaultGridRowColor,
      color: tokens.colors.disabledFontColour,
      WebkitTextFillColor: tokens.colors.disabledFontColour,
    },
    "& .MuiSelect-filled.Mui-disabled": {
      backgroundColor: tokens.colors.defaultGridRowColor,
      color: tokens.colors.disabledFontColour,
      WebkitTextFillColor: tokens.colors.disabledFontColour,
    },
  };

  const numericInputProps = {
    disableUnderline: true,
    sx: commonTextFieldStyle,
  };

  // Handler for NumericFormat changes.
  const handleNumericChange = (name) => (values) => {
    if (!readOnly) {
      handleBondCharacteristicChange({
        target: { name, value: values.floatValue },
      });
    }
  };

  // Add helper to provide min/max based on field name
  const getNumericLimits = (name) => {
    if (name.includes("coupon")) return { min: 0, max: 100 };
    if (name.includes("price")) return { min: 0, max: 150 };
    if (name.includes("yield")) return { min: -100, max: 1000 };
    if (name.includes("maturity")) return { min: 0, max: 50 };
    if (name === "duration") return { min: 0, max: 50 };
    return { min: -Infinity, max: Infinity };
  };

  // Render a pair of numeric inputs (min/max) with error indication
  const renderNumericRange = (minName, maxName) => {
    const hasMinError = !!rangeErrors[minName];
    const hasMaxError = !!rangeErrors[maxName];
    const shouldShowMinError =
      hasMinError && (activeField === minName || activeField === null);
    const shouldShowMaxError =
      hasMaxError && (activeField === maxName || activeField === null);

    // Determine if this is the maturity field
    const isMaturityField = minName === "maturityMin" && maxName === "maturityMax";
    const inputWidth = isMaturityField ? "97px" : "127px";

    // For maturity field, align like ComplexFilterComponents
    if (isMaturityField) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* Min box */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <NumericFormat
              customInput={TextField}
              thousandSeparator=","
              value={bondCharacteristicData[minName] || ""}
              onValueChange={handleNumericChange(minName)}
              placeholder="Min"
              onFocus={handleFieldFocus(minName)}
              onBlur={handleFieldBlur}
              InputProps={{
                disableUnderline: true,
                sx: {
                  ...commonTextFieldStyle,
                  ...(shouldShowMinError &&
                    bondCharacteristicData[minName] && {
                      border: "1px solid #f44336 !important",
                      backgroundColor: `${tokens.colors.white} !important`, // replaced hardcoded rgba
                    }),
                    paddingRight: "0px !important",
                },
                endAdornment:
                  shouldShowMinError &&
                  bondCharacteristicData[minName] && (
                    <InputAdornment position="end">
                      <Tooltip title={rangeErrors[minName]} placement="top">
                        <ErrorOutlineIcon
                          sx={{
                            color: tokens.palette.danger,
                            fontSize: "16px",
                            cursor: "help",
                          }}
                        />
                      </Tooltip>
                    </InputAdornment>
                  ),
              }}
              sx={{
                width: inputWidth,
                paddingRight: "0px !important",
                ...enhancedDisabledStyle,
                ...(shouldShowMinError &&
                  bondCharacteristicData[minName] && {
                    "& .MuiFilledInput-root": {
                      border: "1px solid #f44336 !important",
                      backgroundColor: `${tokens.colors.white} !important`,
                    },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#f44336 !important",
                        borderWidth: "1px !important",
                      },
                      "&:hover fieldset": {
                        borderColor: "#f44336 !important",
                        borderWidth: "1px !important",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#f44336 !important",
                        borderWidth: "1px !important",
                      },
                    },
                  }),
              }}
              disabled={readOnly}
              decimalScale={5}
              isAllowed={(values) => {
                const { floatValue } = values;
                const { min, max } = getNumericLimits(minName);
                return floatValue === undefined || (floatValue >= min && floatValue <= max);
              }}
            />
            <Typography sx={{ ml: 1, minWidth: "22px" }}>Yr</Typography>
          </Box>
          {/* To label - use same simple styling as other numeric ranges */}
          <Typography>To</Typography>
          {/* Max box */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <NumericFormat
              customInput={TextField}
              thousandSeparator=","
              value={bondCharacteristicData[maxName] || ""}
              onValueChange={handleNumericChange(maxName)}
              placeholder="Max"
              onFocus={handleFieldFocus(maxName)}
              onBlur={handleFieldBlur}
              InputProps={{
                disableUnderline: true,
                sx: {
                  ...commonTextFieldStyle,
                  ...(shouldShowMaxError &&
                    bondCharacteristicData[maxName] && {
                      border: "1px solid #f44336 !important",
                      backgroundColor: `${tokens.colors.white} !important`, // replaced hardcoded rgba
                    }),
                },
                endAdornment:
                  shouldShowMaxError &&
                  bondCharacteristicData[maxName] && (
                    <InputAdornment position="end">
                      <Tooltip title={rangeErrors[maxName]} placement="top">
                        <ErrorOutlineIcon
                          sx={{
                            color: tokens.palette.danger,
                            fontSize: "16px",
                            cursor: "help",
                            mr: 1,
                          }}
                        />
                      </Tooltip>
                    </InputAdornment>
                  ),
              }}
              sx={{
                width: inputWidth,
                ...enhancedDisabledStyle,
                ...(shouldShowMaxError &&
                  bondCharacteristicData[maxName] && {
                    "& .MuiFilledInput-root": {
                      border: "1px solid #f44336 !important",
                      backgroundColor: `${tokens.colors.white} !important`,
                    },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#f44336 !important",
                        borderWidth: "1px !important",
                      },
                      "&:hover fieldset": {
                        borderColor: "#f44336 !important",
                        borderWidth: "1px !important",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#f44336 !important",
                        borderWidth: "1px !important",
                      },
                    },
                  }),
              }}
              disabled={readOnly}
              decimalScale={5}
              isAllowed={(values) => {
                const { floatValue } = values;
                const { min, max } = getNumericLimits(maxName);
                return floatValue === undefined || (floatValue >= min && floatValue <= max);
              }}
            />
            <Typography sx={{ ml: 1, minWidth: "22px" }}>Yr</Typography>
          </Box>
        </Box>
      );
    }

    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={{ position: "relative", display: "flex", alignItems: "center" }}>
          <NumericFormat
            customInput={TextField}
            thousandSeparator=","
            value={bondCharacteristicData[minName] || ""}
            onValueChange={handleNumericChange(minName)}
            placeholder="Min"
            onFocus={handleFieldFocus(minName)}
            onBlur={handleFieldBlur}
            InputProps={{
              disableUnderline: true,
              sx: {
                ...commonTextFieldStyle,
                ...(shouldShowMinError &&
                  bondCharacteristicData[minName] && {
                    border: "1px solid #f44336 !important",
                    backgroundColor: `${tokens.colors.white} !important`, // replaced hardcoded rgba
                  }),
              },
              endAdornment:
                shouldShowMinError && bondCharacteristicData[minName] && (
                  <InputAdornment position="end">
                    <Tooltip title={rangeErrors[minName]} placement="top">
                      <ErrorOutlineIcon
                        sx={{
                          color: tokens.palette.danger,
                          fontSize: "16px",
                          cursor: "help",
                          mr: 1,
                        }}
                      />
                    </Tooltip>
                  </InputAdornment>
                ),
            }}
            sx={{
              width: "127px",
              ...enhancedDisabledStyle,
              ...(shouldShowMinError &&
                bondCharacteristicData[minName] && {
                  "& .MuiFilledInput-root": {
                    border: "1px solid #f44336 !important",
                    backgroundColor: `${tokens.colors.white} !important`,
                  },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "#f44336 !important",
                      borderWidth: "1px !important",
                    },
                    "&:hover fieldset": {
                      borderColor: "#f44336 !important",
                      borderWidth: "1px !important",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#f44336 !important",
                      borderWidth: "1px !important",
                    },
                  },
                }),
            }}
            disabled={readOnly}
            decimalScale={5}
            isAllowed={(values) => {
              const { floatValue } = values;
              const { min, max } = getNumericLimits(minName);
              return floatValue === undefined || (floatValue >= min && floatValue <= max);
            }}
          />
        </Box>
        <Typography>To</Typography>
        <Box sx={{ position: "relative", display: "flex", alignItems: "center" }}>
          <NumericFormat
            customInput={TextField}
            thousandSeparator=","
            value={bondCharacteristicData[maxName] || ""}
            onValueChange={handleNumericChange(maxName)}
            placeholder="Max"
            onFocus={handleFieldFocus(maxName)}
            onBlur={handleFieldBlur}
            InputProps={{
              disableUnderline: true,
              sx: {
                ...commonTextFieldStyle,
                ...(shouldShowMaxError &&
                  bondCharacteristicData[maxName] && {
                    border: "1px solid #f44336 !important",
                    backgroundColor: `${tokens.colors.white} !important`, // replaced hardcoded rgba
                  }),
              },
              endAdornment:
                shouldShowMaxError && bondCharacteristicData[maxName] && (
                  <InputAdornment position="end">
                    <Tooltip title={rangeErrors[maxName]} placement="top">
                      <ErrorOutlineIcon
                        sx={{
                          color: tokens.palette.danger,
                          fontSize: "16px",
                          cursor: "help",
                          mr: 1,
                        }}
                      />
                    </Tooltip>
                  </InputAdornment>
                ),
            }}
            sx={{
              width: "127px",
              ...enhancedDisabledStyle,
              ...(shouldShowMaxError &&
                bondCharacteristicData[maxName] && {
                  "& .MuiFilledInput-root": {
                    border: "1px solid #f44336 !important",
                    backgroundColor: `${tokens.colors.white} !important`,
                  },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "#f44336 !important",
                      borderWidth: "1px !important",
                    },
                    "&:hover fieldset": {
                      borderColor: "#f44336 !important",
                      borderWidth: "1px !important",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#f44336 !important",
                      borderWidth: "1px !important",
                    },
                  },
                }),
            }}
            disabled={readOnly}
            decimalScale={5}
            isAllowed={(values) => {
              const { floatValue } = values;
              const { min, max } = getNumericLimits(maxName);
              return floatValue === undefined || (floatValue >= min && floatValue <= max);
            }}
          />
        </Box>
      </Box>
    );
  };

  // Update the date picker error display too
  const renderDatePicker = (name, placeholder, width = "178px") => {
    const hasError = !!dateErrors[name];
    // Show error only on active field, or on both if no field is active
    const shouldShowError =
      hasError && (activeField === name || activeField === null);

    return (
      <Box sx={{ position: "relative" }}>
        <DatePicker
          format="DD/MM/YYYY"
          value={
            bondCharacteristicData[name]
              ? dayjs(bondCharacteristicData[name]).utc()
              : null
          }
          onChange={
            readOnly
              ? null
              : (newValue) => {
                  setActiveField(name); // Set active field on change
                  handleBondCharacteristicChange({
                    target: {
                      name,
                      value: newValue ? newValue.utc().format("YYYY-MM-DD") : "",
                    },
                  });
                }
          }
          sx={
            {
              // ...existing styles...
            }
          }
          disabled={readOnly}
          renderInput={(params) => (
            <TextField
              {...params}
              onFocus={handleFieldFocus(name)}
              onBlur={handleFieldBlur}
              inputProps={{ ...params.inputProps, placeholder }}
              variant="filled"
              error={
                shouldShowError && bondCharacteristicData[name] ? true : false
              }
              sx={{
                width,
                ...enhancedDisabledStyle,
                // ...existing styles...
              }}
              InputProps={{
                ...params.InputProps,
                disableUnderline: true,
                sx: {
                  ...commonTextFieldStyle,
                  backgroundColor: tokens.colors.white,
                  // ...existing styles...
                },
                endAdornment: (
                  <>
                    {params.InputProps.endAdornment}
                    {shouldShowError &&
                      bondCharacteristicData[name] && (
                        <InputAdornment position="end">
                          <Tooltip title={dateErrors[name]} placement="top">
                            <ErrorOutlineIcon
                              sx={{
                                color: tokens.palette.danger,
                                fontSize: "16px",
                                cursor: "help",
                                mr: 1,
                              }}
                            />
                          </Tooltip>
                        </InputAdornment>
                      )}
                  </>
                ),
              }}
            />
          )}
        />
      </Box>
    );
  };

  const renderInput = () => {
    switch (field) {
      case "couponRange":
        return renderNumericRange("couponMin", "couponMax");

      case "price":
      case "yield":
        return renderNumericRange(`${field}Min`, `${field}Max`);

      case "maturityRanges":
        return renderNumericRange("maturityMin", "maturityMax");

      case "duration":
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {!readOnly ? (
              <>
                <Select
                  value={bondCharacteristicData.durationComparison || ""}
                  onChange={
                    readOnly
                      ? undefined
                      : (e) => {
                          const operator =
                            e.target.value === "greaterThanOrEqual"
                              ? ">="
                              : e.target.value === "lessThanOrEqual"
                              ? "<="
                              : "";
                          const currentDuration =
                            bondCharacteristicData.duration?.replace(
                              /[<>=]/g,
                              ""
                            ) || "";

                          handleBondCharacteristicChange({
                            target: {
                              name: "duration",
                              value: currentDuration
                                ? `${operator}${currentDuration}`
                                : "",
                            },
                          });

                          handleBondCharacteristicChange({
                            target: {
                              name: "durationComparison",
                              value: e.target.value,
                            },
                          });
                        }
                  }
                  sx={{
                    width: "143px",
                    height: "40px",
                    ...commonTextFieldStyle,
                    ...enhancedDisabledStyle,
                  }}
                  displayEmpty
                >
                  <MenuItem value="">{"Select"}</MenuItem>
                  <MenuItem value="greaterThanOrEqual">{">="}</MenuItem>
                  <MenuItem value="lessThanOrEqual">{"<="}</MenuItem>
                </Select>
                <NumericFormat
                  customInput={TextField}
                  thousandSeparator=","
                  value={
                    bondCharacteristicData.duration?.replace(/[<>=]/g, "") || ""
                  }
                  onValueChange={(values) => {
                    if (!readOnly) {
                      const operator =
                        bondCharacteristicData.durationComparison ===
                        "greaterThanOrEqual"
                          ? ">="
                          : bondCharacteristicData.durationComparison ===
                            "lessThanOrEqual"
                          ? "<="
                          : "";
                      handleBondCharacteristicChange({
                        target: {
                          name: "duration",
                          value: values.floatValue
                            ? `${operator}${values.floatValue}`
                            : "",
                        },
                      });
                    }
                  }}
                  placeholder="duration"
                  InputProps={{
                    disableUnderline: true,
                    sx: commonTextFieldStyle,
                  }}
                  sx={{ width: "127px", ...enhancedDisabledStyle }}
                  disabled={!bondCharacteristicData.durationComparison}
                  decimalScale={5}
                  isAllowed={(values) => {
                    const { floatValue } = values;
                    const { min, max } = getNumericLimits("duration");
                    return floatValue === undefined || (floatValue >= min && floatValue <= max);
                  }}
                />
              </>
            ) : (
              <TextField
                value={bondCharacteristicData.duration || ""}
                disabled
                InputProps={{
                  disableUnderline: true,
                  sx: commonTextFieldStyle,
                  readOnly: true,
                }}
                sx={{
                  width: "127px",
                  ...enhancedDisabledStyle,
                }}
              />
            )}
          </Box>
        );

      case "callable":
      case "zeroCoupon":
      case "insured":
      case "taxable":
      case "altMinTax":
        return (
          <Box sx={{ width: "100%" }}> {/* Add container with min width */}
            <RadioGroup
              row
              name={field}
              value={bondCharacteristicData[field] || ""}
              onChange={readOnly ? undefined : handleBondCharacteristicChange}
              sx={{ 
                display: "flex", 
                flexDirection: "row", 
                flexWrap: "nowrap", // Prevent wrapping
                gap: 1,
                width: "100%"
              }}
            >
              {fieldOptions[field]?.map((option) => (
                <FormControlLabel
                  key={option}
                  value={option}
                  control={
                    <Radio
                      disabled={readOnly}
                      sx={{
                        "&.Mui-disabled": { color: "primary.main", opacity: 1 },
                      }}
                    />
                  }
                  label={option}
                  sx={{ 
                    marginRight: 2,
                    whiteSpace: "nowrap", // Prevent label wrapping
                  }}
                />
              ))}
            </RadioGroup>
          </Box>
        );
      case "paymentFrequency":
      case "sector":
      case "incomeSource": {
        return (
          <Select
            fullWidth
            name={field}
            value={bondCharacteristicData[field] || ""}
            onChange={readOnly ? undefined : handleBondCharacteristicChange}
            inputProps={{
              style: { width: "100%" },
            }}
            sx={{
              width: "287px",
              height: "40px",
              ...commonTextFieldStyle,
              ...enhancedDisabledStyle,
            }}
            displayEmpty
            disabled={readOnly}
          >
            <MenuItem value="">Select {getFieldLabel()}</MenuItem>
            {fieldOptions[field]?.map((option) => (
              <MenuItem key={String(option)} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        );
      }

      case "securityCharge": {
        const SC_ALL = "ALL";
        const baseOptions = securityChargeOptions.length > 0
          ? securityChargeOptions
          : (fieldOptions[field] || []);
        const secChargeOptions = [SC_ALL, ...baseOptions.filter(o => o !== SC_ALL)];

        const rawValue = bondCharacteristicData.securityCharge;
        const selectedValues = Array.isArray(rawValue)
          ? (rawValue.length === 0 ? [SC_ALL] : rawValue)
          : rawValue ? [rawValue] : [SC_ALL];

        const hasAllSelected = selectedValues.includes(SC_ALL);
        const displayValues = hasAllSelected ? [SC_ALL] : selectedValues;
        const previewValue = displayValues.join(", ");

        const handleSecurityChargeChange = (event, newValue, reason, details) => {
          if (readOnly) return;
          const changedOption = details?.option;
          const normalizedSelection = Array.from(
            new Set((newValue || []).map(v => String(v).trim()).filter(v => v))
          );

          let nextValues;
          if (reason === "clear") {
            nextValues = [SC_ALL];
          } else if (changedOption === SC_ALL) {
            const nowHasAll = normalizedSelection.includes(SC_ALL);
            nextValues = nowHasAll ? [SC_ALL] : [SC_ALL];
          } else {
            const withoutAll = normalizedSelection.filter(v => v !== SC_ALL);
            nextValues = withoutAll.length > 0 ? withoutAll : [SC_ALL];
          }

          handleBondCharacteristicChange({
            target: { name: "securityCharge", value: nextValues },
          });
        };

        return (
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={secChargeOptions}
            value={displayValues}
            openOnFocus
            onChange={handleSecurityChargeChange}
            onOpen={() => setIsSecurityChargeOpen(true)}
            onClose={() => setIsSecurityChargeOpen(false)}
            disabled={readOnly}
            isOptionEqualToValue={(option, value) => option === value}
            getOptionLabel={(option) => option || ""}
            renderOption={(props, option, { selected }) => {
              const hideCheckbox = option === SC_ALL;
              return (
                <Box
                  component="li"
                  {...props}
                  sx={{
                    pl: "10px",
                    py: 1,
                    display: "flex",
                    alignItems: "center",
                    fontSize: "16px",
                    borderRadius: 0,
                  }}
                >
                  {!hideCheckbox && (
                    <Checkbox
                      checked={selected}
                      sx={{ width: "20px", height: "20px", padding: 0, marginRight: "8px", borderRadius: 0 }}
                      disableRipple
                    />
                  )}
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontWeight: selected ? "bold" : "normal",
                      color: selected ? tokens.colors.fontColour : tokens.colors.fontColour85,
                      lineHeight: "20px",
                    }}
                  >
                    {option}
                  </Typography>
                </Box>
              );
            }}
            renderTags={() => null}
            renderInput={(params) => (
              <Box sx={{ position: "relative" }}>
                <TextField
                  {...params}
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    disableUnderline: true,
                    sx: { ...commonTextFieldStyle, height: "40px" },
                  }}
                  sx={{ width: "287px" }}
                />
                {!isSecurityChargeOpen && !!previewValue && (
                  <Typography
                    sx={{
                      position: "absolute",
                      left: "12px",
                      right: "55px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      pointerEvents: "none",
                      color: readOnly ? tokens.colors.disabledFontColour : tokens.colors.fontColour,
                      fontSize: "0.95rem",
                      lineHeight: "1.25rem",
                    }}
                  >
                    {previewValue}
                  </Typography>
                )}
              </Box>
            )}
            sx={{
              width: "287px",
              ...enhancedDisabledStyle,
              "& .MuiFilledInput-root.MuiAutocomplete-inputRoot": {
                ...commonTextFieldStyle,
                paddingTop: "0 !important",
                paddingRight: "55px !important",
                paddingBottom: "0 !important",
                paddingLeft: "0 !important",
                minHeight: "auto",
                height: "40px",
              },
              "& .MuiAutocomplete-inputRoot .MuiAutocomplete-input": {
                padding: "0 12px !important",
                minWidth: 0,
                textOverflow: "ellipsis",
                color: isSecurityChargeOpen ? tokens.colors.fontColour : "transparent",
                caretColor: isSecurityChargeOpen ? tokens.colors.fontColour : "transparent",
              },
              "& .MuiAutocomplete-endAdornment": {
                top: "50%",
                transform: "translateY(-50%)",
                right: "8px",
              },
              "& .MuiAutocomplete-inputRoot": { flexWrap: "nowrap" },
            }}
          />
        );
      }
      case "state":
        return (
          <Select
            fullWidth
            name={field}
            value={bondCharacteristicData[field] || ""}
            onChange={readOnly ? undefined : handleBondCharacteristicChange}
            //the text written inside width 100%
            inputProps={{
              style: { width: "100%" },
            }}
            sx={{
              width: "287px",
              height: "40px",
              ...commonTextFieldStyle,
              ...enhancedDisabledStyle,
            }}
            displayEmpty
            disabled={readOnly}
          >
            <MenuItem value="">Select {getFieldLabel()}</MenuItem>
            {stateList.map((state) => (
              <MenuItem key={state.value} value={state.value}>
                {state.name}
              </MenuItem>
            ))}
          </Select>
        );

      case "minimumRating":
        return (
          <Select
            fullWidth
            name={field}
            value={bondCharacteristicData[field] || ""}
            onChange={readOnly ? undefined : handleBondCharacteristicChange}
            inputProps={{
              style: { width: "100%" },
            }}
            sx={{
              width: "287px",
              height: "40px",
              ...commonTextFieldStyle,
              ...enhancedDisabledStyle,
            }}
            displayEmpty
            disabled={readOnly}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 240,
                  overflow: 'auto'
                }
              }
            }}
          >
            <MenuItem value="">Select {getFieldLabel()}</MenuItem>
            {(spRatingOptions.length > 0 ? spRatingOptions : fieldOptions[field])?.map((option) =>
              typeof option === "object" ? (
                <MenuItem key={option.rating} value={option.rating}>
                  {option.rating}
                </MenuItem>
              ) : (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              )
            )}
          </Select>
        );

      case "nextCallDate":
        return (
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ 
              width: "100%", 
              minWidth: "200px", // Add minimum width
              position: "relative",
              zIndex: 1000, // Ensure date picker appears above other elements
            }}>
              {renderDatePicker(
                "nextCallDate",
                "Select Next Call Date",
                "200px" // Increase width
              )}
            </Box>
          </LocalizationProvider>
        );

      case "underlyingRating":
        return (
          <TextField
            fullWidth
            variant="filled"
            name={field}
            value={bondCharacteristicData[field] || ""}
            onChange={readOnly ? undefined : handleBondCharacteristicChange}
            placeholder={`${getFieldLabel()}`}
            InputProps={{
              disableUnderline: true,
              sx: {
                ...commonTextFieldStyle,
                width: "287px",
                // border: "1px solid rgba(255, 255, 255, 0.2)",
                //transparent color when active
                bgcolor: "transparent",
                "&:hover": {
                  bgcolor: "transparent",
                },
                "&.Mui-focused": {
                  bgcolor: "transparent",
                },
              },
              readOnly: readOnly,
            }}
            //transparent color when active
            // border: "1px solid rgba(255, 255, 255, 0.2)",
            sx={{
              "&.Mui-disabled": {
                color: tokens.colors.disabledFontColour,
                WebkitTextFillColor: tokens.colors.disabledFontColour,
                opacity: 1,
                backgroundColor: tokens.colors.defaultGridRowColor,
              },
              "& .MuiFilledInput-root.Mui-disabled": {
                backgroundColor: tokens.colors.defaultGridRowColor,
                color: tokens.colors.disabledFontColour,
                WebkitTextFillColor: tokens.colors.disabledFontColour,
              },
              "& .MuiSelect-filled.Mui-disabled": {
                backgroundColor: tokens.colors.defaultGridRowColor,
                color: tokens.colors.disabledFontColour,
                WebkitTextFillColor: tokens.colors.disabledFontColour,
              },
            }}
            disabled={readOnly}
          />
        );

      case "issuer":
        const hasIssuerError = issuerValidationError && bondCharacteristicData.issuer;

        return (
          <Autocomplete
            value={bondCharacteristicData.issuer || ""}
            inputValue={bondCharacteristicData.issuer || ""}
            onChange={(event, newValue) => {
              if (!readOnly) {
                handleBondCharacteristicChange({
                  target: { name: "issuer", value: newValue || "" }
                });
              }
            }}
            onInputChange={(event, newInputValue) => {
              if (!readOnly) {
                handleBondCharacteristicChange({
                  target: { name: "issuer", value: newInputValue }
                });
              }
            }}
            options={issuerOptions || []}
            renderInput={(params) => (
              <TextField 
                {...params} 
                placeholder="Select Issuer" 
                fullWidth
                error={hasIssuerError}
                // helperText={hasIssuerError ? "No matching issuer found" : ""}
                InputProps={{
                  ...params.InputProps,
                  disableUnderline: true,
                  sx: {
                    ...commonTextFieldStyle,
                    height: "40px",
                    ...(hasIssuerError && {
                      border: "1px solid #f44336 !important",
                      backgroundColor: `${tokens.colors.white} !important`, // replaced hardcoded rgba
                    }),
                  }
                }}
                sx={{
                  width: "287px",
                  ...(hasIssuerError && {
                    "& .MuiFormHelperText-root": {
                      color: "#f44336",
                      marginLeft: "14px",
                      marginTop: "4px",
                    },
                  }),
                }}
              />
            )}
            sx={{ 
              width: "287px",
              ...enhancedDisabledStyle,
              "& .MuiAutocomplete-inputRoot": {
                ...commonTextFieldStyle,
                height: "40px",
                paddingLeft: "14px",
                ...(hasIssuerError && {
                  border: "1px solid #f44336 !important",
                  backgroundColor: `${tokens.colors.white} !important`, // replaced hardcoded rgba
                }),
              },
              "& .MuiAutocomplete-endAdornment": {
                right: "9px",
                top: "calc(50% - 14px)"
              },
              "& .MuiAutocomplete-input": {
                padding: "0px !important",
              }
            }}
            freeSolo
            disableClearable={true}
            forcePopupIcon={true}
            popupIcon={null}
            disabled={readOnly}
          />
        );

      case "minimumRatingMoody":
        return (
          <Select
            fullWidth
            name={field}
            value={bondCharacteristicData[field] || ""}
            onChange={readOnly ? undefined : handleBondCharacteristicChange}
            inputProps={{
              style: { width: "100%" },
            }}
            sx={{
              width: "287px",
              height: "40px",
              ...commonTextFieldStyle,
              ...enhancedDisabledStyle,
            }}
            displayEmpty
            disabled={readOnly}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 240,
                  overflow: 'auto'
                }
              }
            }}
          >
            <MenuItem value="">Select {getFieldLabel()}</MenuItem>
            {(moodyRatingOptions.length > 0 ? moodyRatingOptions : [])?.map((option) =>
              typeof option === "object" ? (
                <MenuItem key={option.rating} value={option.rating}>
                  {option.rating}
                </MenuItem>
              ) : (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              )
            )}
          </Select>
        );

      default:
        return (
          <TextField
            fullWidth
            name={field}
            value={bondCharacteristicData[field] || ""}
            onChange={readOnly ? undefined : handleBondCharacteristicChange}
            placeholder={`Enter ${getFieldLabel()}`}
            sx={{
              height: "40px",
              ...commonTextFieldStyle,
              ...enhancedDisabledStyle,
            }}
            InputProps={{ readOnly: readOnly }}
            disabled={readOnly}
          />
        );
    }
  };

  return (
    <LabeledField
      readOnly={readOnly}
      checked={isChecked}
      label={getFieldLabel()}
      onCheckboxChange={onCheckboxChange(panelName, field)}
      datePreview={field === "maturityRanges" ? maturityDatePreview : null}
    >
      {renderInput()}
    </LabeledField>
  );
};

export default BondCharacteristicField;
